import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Target, ListChecks, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";

const STATUS_COLORS = {
  planned: "bg-[#111827] text-slate-300",
  active: "bg-emerald-50 text-emerald-700",
  completed: "bg-blue-50 text-blue-700",
};

const Sprints = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailSprint, setDetailSprint] = useState(null);  // sprint object → opens sprint task drawer
  const [form, setForm] = useState(() => ({
    name: "",
    goal: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    status: "planned",
  }));

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/sprints?project_id=${projectId}`),
    ]);
    setProject(p.data);
    setSprints(s.data);
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const create = async () => {
    if (!form.name.trim()) return;
    const { data } = await api.post("/sprints", { project_id: projectId, ...form });
    setSprints([...sprints, { ...data, committed_points: 0, completed_points: 0, task_count: 0 }]);
    setOpen(false);
    setForm({ name: "", goal: "", start_date: new Date().toISOString().slice(0, 10), end_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10), status: "planned" });
    toast.success("Sprint created");
  };

  const updateStatus = async (id, status) => {
    setSprints((prev) => prev.map((s) => (s.sprint_id === id ? { ...s, status } : s)));
    await api.patch(`/sprints/${id}`, { status });
  };

  const remove = async (id) => {
    await api.delete(`/sprints/${id}`);
    setSprints((prev) => prev.filter((s) => s.sprint_id !== id));
    toast.success("Sprint removed");
  };

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `Sprints · ${project.name}` : "Sprints"}
        title="Sprint Planning"
        description="Time-boxed iterations with velocity tracking using story points."
        actions={
          <div className="flex gap-2">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-project-from-sprints">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-sprint-btn">
                  <Plus className="w-4 h-4 mr-1" /> New sprint
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-gray-900 sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight">New sprint</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Sprint name</Label>
                    <Input className="rounded-none mt-1" placeholder="e.g. Sprint 1" data-testid="sprint-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Sprint goal</Label>
                    <Textarea className="rounded-none mt-1" data-testid="sprint-goal-input" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Start</Label>
                      <Input type="date" className="rounded-none mt-1" data-testid="sprint-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">End</Label>
                      <Input type="date" className="rounded-none mt-1" data-testid="sprint-end-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="sprint-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planned">Planned</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-sprint">Create sprint</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-6">
        {/* Velocity chart */}
        {sprints.filter((s) => s.committed_points > 0 || s.completed_points > 0).length >= 2 && (
          <div className="border border-[#1F2937] bg-[#0D1117] p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Sprint velocity</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={sprints.slice().reverse().map((s) => ({
                  name: s.name.length > 14 ? s.name.slice(0, 13) + "…" : s.name,
                  committed: s.committed_points || 0,
                  completed: s.completed_points || 0,
                }))}
                barGap={4}
                barSize={20}
              >
                <CartesianGrid stroke="#1F2937" vertical={false} />
                <XAxis dataKey="name" stroke="#4B5563" fontSize={10} />
                <YAxis stroke="#4B5563" fontSize={10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 0, color: "#fff", fontSize: 12 }}
                  labelStyle={{ color: "#9CA3AF" }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#9CA3AF" }} />
                <Bar dataKey="committed" fill="#374151" name="Committed SP" />
                <Bar dataKey="completed" fill="#002FA7" name="Completed SP" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {sprints.length === 0 ? (
          <div className="border border-dashed border-[#374151] p-16 text-center">
            <Target className="w-8 h-8 text-[#002FA7] mx-auto" strokeWidth={1.5} />
            <h3 className="font-display font-black text-2xl tracking-tight mt-4">No sprints yet</h3>
            <p className="text-gray-500 mt-2">Create your first sprint to start planning by story points.</p>
            <Button onClick={() => setOpen(true)} className="rounded-none mt-6 bg-[#002FA7] hover:bg-[#002480]" data-testid="empty-create-sprint-btn">
              <Plus className="w-4 h-4 mr-1" /> New sprint
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937] border border-[#1F2937]">
            {sprints.map((s) => {
              const velocityPct = s.committed_points ? Math.round((s.completed_points / s.committed_points) * 100) : 0;
              return (
                <div key={s.sprint_id} className="bg-[#0D1117] p-6 group cursor-pointer hover:bg-[#030712] transition-colors" onClick={() => setDetailSprint(s)} data-testid={`sprint-card-${s.sprint_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${STATUS_COLORS[s.status]}`}>{s.status}</div>
                      <h3 className="font-display font-bold text-xl tracking-tight mt-3">{s.name}</h3>
                      {s.goal && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{s.goal}</p>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); remove(s.sprint_id); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600" data-testid={`delete-sprint-${s.sprint_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="mt-4 text-[11px] font-mono text-gray-500">
                    {new Date(s.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} → {new Date(s.end_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>

                  <div className="mt-5 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="uppercase tracking-[0.2em] text-gray-500">Velocity</span>
                      <span className="font-mono text-slate-300" data-testid={`sprint-velocity-${s.sprint_id}`}>
                        {s.completed_points || 0} / {s.committed_points || 0} SP · {velocityPct}%
                      </span>
                    </div>
                    <div className="h-2 bg-[#111827]">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${velocityPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500 mt-2">
                      <span data-testid={`sprint-task-count-${s.sprint_id}`}>
                        {s.completed_task_count || 0}/{s.task_count || 0} tasks done
                      </span>
                      <Select value={s.status} onValueChange={(v) => updateStatus(s.sprint_id, v)}>
                        <SelectTrigger onClick={(e) => e.stopPropagation()} className="rounded-none h-7 w-32 text-xs" data-testid={`sprint-status-${s.sprint_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent onClick={(e) => e.stopPropagation()}>
                          <SelectItem value="planned">Planned</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SprintDetailDialog
        key={detailSprint?.sprint_id}
        sprint={detailSprint}
        onClose={() => setDetailSprint(null)}
        onChanged={load}
      />
    </div>
  );
};

const SprintDetailDialog = ({ sprint, onClose, onChanged }) => {
  // tasks === null  → still loading initial fetch
  // tasks === []    → loaded, empty
  // tasks === [...] → loaded, populated
  const [tasks, setTasks] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskPoints, setNewTaskPoints] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sprint) return undefined;
    let cancelled = false;
    api.get(`/sprints/${sprint.sprint_id}/tasks`).then(({ data }) => {
      if (!cancelled) setTasks(data);
    }).catch(() => {
      if (!cancelled) setTasks([]);
    });
    return () => { cancelled = true; };
  }, [sprint]);

  if (!sprint) return null;

  const loading = tasks === null;
  const safeTasks = tasks || [];
  const completed = safeTasks.filter((t) => t.status === "done").length;
  const totalPoints = safeTasks.reduce((acc, t) => acc + (Number(t.story_points) || 0), 0);
  const donePoints = safeTasks
    .filter((t) => t.status === "done")
    .reduce((acc, t) => acc + (Number(t.story_points) || 0), 0);

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sprints/${sprint.sprint_id}/tasks`, {
        title,
        story_points: Number(newTaskPoints) || 0,
      });
      setTasks((prev) => [...prev, data]);
      setNewTaskTitle("");
      setNewTaskPoints("");
      toast.success("Task added to sprint");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add task");
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = async (task) => {
    const next = task.status === "done" ? "todo" : "done";
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? { ...t, status: next } : t)));
    try {
      await api.patch(`/tasks/${task.task_id}/status`, { status: next });
      onChanged?.();
    } catch (e) {
      // Roll back
      setTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? { ...t, status: task.status } : t)));
      toast.error(e.response?.data?.detail || "Couldn't update task");
    }
  };

  const removeTask = async (task) => {
    if (!confirm(`Delete "${task.title}"?`)) return;
    const prev = tasks;
    setTasks((p) => p.filter((t) => t.task_id !== task.task_id));
    try {
      await api.delete(`/tasks/${task.task_id}`);
      onChanged?.();
    } catch (e) {
      setTasks(prev);
      toast.error(e.response?.data?.detail || "Couldn't delete task");
    }
  };

  return (
    <Dialog open={!!sprint} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-gray-900 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sprint-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gray-500">
            <ListChecks className="w-3.5 h-3.5" />
            Sprint backlog
          </div>
          <DialogTitle className="font-display tracking-tight text-2xl mt-1">{sprint.name}</DialogTitle>
          {sprint.goal && <p className="text-sm text-gray-500">{sprint.goal}</p>}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-xs">
            <span className="text-gray-500">
              <span className="font-mono text-gray-900 font-semibold" data-testid="sprint-detail-task-progress">{completed}/{safeTasks.length}</span> tasks done
            </span>
            <span className="text-gray-500">
              <span className="font-mono text-gray-900 font-semibold">{donePoints}/{totalPoints}</span> SP completed
            </span>
            <span className="text-gray-500">
              {new Date(sprint.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} → {new Date(sprint.end_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          </div>
        </DialogHeader>

        <div className="mt-2 border border-[#1F2937] bg-[#030712] p-3 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500">New task</Label>
            <Input
              className="rounded-none mt-1 h-9 bg-[#0D1117] text-white"
              placeholder="e.g. Wire up Salesforce auth endpoint"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) addTask(); }}
              data-testid="sprint-new-task-title"
            />
          </div>
          <div className="w-20">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500">SP</Label>
            <Input
              className="rounded-none mt-1 h-9 bg-[#0D1117] text-white"
              type="number"
              min={0}
              placeholder="0"
              value={newTaskPoints}
              onChange={(e) => setNewTaskPoints(e.target.value)}
              data-testid="sprint-new-task-points"
            />
          </div>
          <Button onClick={addTask} disabled={busy || !newTaskTitle.trim()} className="rounded-none h-9 bg-[#002FA7] hover:bg-[#002480]" data-testid="sprint-add-task-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-xs text-gray-500">Loading tasks…</div>
        ) : safeTasks.length === 0 ? (
          <div className="py-12 text-center border border-dashed border-[#374151] mt-3">
            <ListChecks className="w-7 h-7 text-gray-300 mx-auto" strokeWidth={1.4} />
            <div className="text-sm text-gray-500 mt-3">No tasks in this sprint yet.</div>
            <div className="text-xs text-gray-400 mt-1">Add one above to get started.</div>
          </div>
        ) : (
          <div className="mt-3 divide-y divide-gray-200 border border-[#1F2937]" data-testid="sprint-task-list">
            {safeTasks.map((t) => {
              const done = t.status === "done";
              return (
                <div
                  key={t.task_id}
                  className={`flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 ${done ? "bg-emerald-900/20" : ""}`}
                  data-testid={`sprint-task-${t.task_id}`}
                >
                  <button
                    onClick={() => toggleTask(t)}
                    className={done ? "text-emerald-600 hover:text-emerald-700" : "text-gray-400 hover:text-[#002FA7]"}
                    title={done ? "Mark not done" : "Mark done"}
                    data-testid={`sprint-task-toggle-${t.task_id}`}
                  >
                    {done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${done ? "line-through text-gray-500" : "text-gray-900"} truncate`}>
                      {t.title}
                    </div>
                    {t.description && !done && (
                      <div className="text-[11px] text-gray-500 truncate">{t.description}</div>
                    )}
                  </div>
                  <input
                    type="number"
                    min={0}
                    value={t.story_points ?? 0}
                    onChange={async (e) => {
                      const pts = Number(e.target.value) || 0;
                      setTasks((prev) => prev.map((x) => x.task_id === t.task_id ? { ...x, story_points: pts } : x));
                      try { await api.patch(`/tasks/${t.task_id}`, { story_points: pts }); onChanged?.(); }
                      catch { /* non-critical */ }
                    }}
                    className="w-14 h-6 text-center text-[10px] font-mono bg-[#111827] border border-[#1F2937] text-gray-300 rounded-sm"
                    title="Story points"
                  />
                  <button
                    onClick={() => removeTask(t)}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete task"
                    data-testid={`sprint-task-delete-${t.task_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-none border-gray-900" data-testid="sprint-detail-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Sprints;
